import { useState, useRef, useEffect } from 'react';
import { Track } from './Track';
import { Controls } from './Controls';
import { ProgressBar } from './ProgressBar';
import { VolumeControl } from './VolumeControl';
import { PlayList } from './PlayList';
import { SpeedControl } from './SpeedControl';
import { motion, AnimatePresence } from 'motion/react';
import List from '../../assets/icons/audioplayer/list.svg';
import chevronDown from '../../assets/icons/chevronDown.svg';
import useMediaQuery from '../../hooks/index';
import { useAudioPlayerContext, Track as TrackType } from './context/AudioPlayerContext';
import { Button } from '../ui/Button';
import SvgIcon from '../ui/SvgIcon';
import CloseCircle from '../../assets/icons/x-circle.svg?react';
import Close from '../../assets/icons/x-mark.svg?react';
import { useFavorite } from "../../hooks/useFavorite";
import { useAuth } from '../../contexts/AuthContext';

export type AudioPlayerProps = {
    track: TrackType;
    tracks: TrackType[];
    onClose?: () => void;
}

export const AudioPlayer = ({ track, tracks, onClose }: AudioPlayerProps) => {
    const isMobileView = useMediaQuery('(max-width: 639px)');
    const [openDrawer, setOpenDrawer] = useState(false);
    const [mobilePlayerOpen, setMobilePlayerOpen] = useState(false);
    const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
    const drawerRef = useRef<HTMLDivElement | null>(null);
    const playlistButtonRef = useRef<HTMLImageElement | null>(null);
    const { currentTrack, setCurrentTrack, setTracks, timeProgress, setTimeProgress, isPlaying, setIsPlaying, audioRef, setDuration, duration, progressBarRef, lastProgressValue, setLastProgressValue } = useAudioPlayerContext();
    const { isAuthenticated, setIsAuthOpen } = useAuth();
    const { toggleFavorite } = useFavorite();

    useEffect(() => {
        setCurrentTrack(track);
        setTracks(tracks);
        audioRef?.current?.load();
        audioRef?.current?.play();
        setIsPlaying(true);
    }, [track, tracks]);

    const handleClickOutside = (event: MouseEvent) => {
       
        if (drawerRef.current && !drawerRef.current.contains(event.target as Node) && playlistButtonRef.current && !playlistButtonRef.current.contains(event.target as Node)) {
            setOpenDrawer(false);
        }
    };

    const toggleMediaToFavorite = (action: 'Add' | 'Remove', type:  'Episode', id: number) => {
        if (!isAuthenticated) {
            setIsAuthOpen(true);
            return;
        }
        toggleFavorite(action, { contentId: id, contentType: type });
        setCurrentTrack(prev => ({ ...prev, isFavorite: action === 'Add' }));
    }

    useEffect(() => {
        if (openDrawer) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDrawer]);

    const toggleSpeedMenu = () => {
        setSpeedMenuOpen((prev) => {
            if (!prev && openDrawer) setOpenDrawer(false);
            return !prev;
        });
    };

    const toggleDrawer = () => {
        setOpenDrawer((prev) => {
            if (!prev && openDrawer) setSpeedMenuOpen(false);
            return !prev;
        });
    };

    const handleTileClick = (e: React.MouseEvent) => {
        if (!(e.target as HTMLElement).closest('.play-button') && !(e.target as HTMLElement).closest('.progress-bar')) {
            setMobilePlayerOpen(true);
        }
    };
    const onLoadedMetadata = () => {
        const seconds = audioRef.current?.duration;
        if (seconds !== undefined) {
            setDuration(seconds);
            if (progressBarRef.current) {
                progressBarRef.current.max = seconds.toString();
            }
        }
    };

    useEffect(() => {
        const updateProgress = () => {
            if (audioRef.current) {
                const currentTime = audioRef.current.currentTime;
                setTimeProgress(currentTime);
                setLastProgressValue(currentTime);
            }
        };

        if (audioRef.current) {
            audioRef.current.addEventListener('timeupdate', updateProgress);
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.removeEventListener('timeupdate', updateProgress);
            }
        };
    }, [audioRef, setTimeProgress]);

    const handleProgressBarClick = (e: React.MouseEvent<HTMLSpanElement>) => {
        if (progressBarRef.current && audioRef.current) {
            const rect = progressBarRef.current.getBoundingClientRect();
            const clickPosition = e.clientX - rect.left;
            const clickRatio = clickPosition / rect.width;
            const newTime = clickRatio * (audioRef.current.duration || 0);

            if (!isNaN(newTime) && newTime >= 0 && newTime <= audioRef.current.duration) {
                audioRef.current.currentTime = newTime;
                setTimeProgress(newTime);
            }
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: -10, opacity: 0 }}
                animate={{ 
                    y: [0, -20, 0],
                    opacity: 1,
                }}
                exit={{ y: 20, opacity: 0 }}
                transition={{ 
                    duration: 0.5,
                    y: {
                        duration: 0.3,
                        ease: "easeInOut"
                    }
                }}
            >
                <div className="relative">
                    <audio
                        src={currentTrack?.src}
                        ref={audioRef}
                        onLoadedMetadata={onLoadedMetadata}
                    />
                    {/* Web & Tablet Design */}
                    <span className="flex" onClick={handleProgressBarClick}>
                        {
                            !isMobileView && <ProgressBar />
                        }
                        {/* <ProgressBar/> */}
                    </span>
                    <div className="hidden sm:flex *:w-full bg-secondary-audio lg:flex-row flex-col justify-between items-center text-white px-6 py-4">
                        {/* First Column */}
                        <div className="flex flex-col gap-2">
                            <Track />
                        </div>
                        {/* Second Column */}
                        <div className="flex justify-center items-center">
                            <Controls isMobileView={!isMobileView} isPlaying={isPlaying} setIsPlaying={setIsPlaying} />
                        </div>
                        {/* Third Column */}
                        <div className="hidden md:flex justify-center lg:justify-end items-center gap-2 text-gray-400">
                            <VolumeControl />
                            <SpeedControl toggleSpeedMenu={toggleSpeedMenu} speedMenuOpen={speedMenuOpen} />
                            <button onClick={toggleDrawer} title="Playlist" >
                                <img ref={playlistButtonRef} src={List} srcSet={`${List} 1x, ${List} 2x`} loading="lazy" alt="playlist" className="w-[48px] h-[48px]" />
                            </button>
                            <button title="Close Player" onClick={() => onClose?.()}><CloseCircle className="text-white" /></button>
                        </div>
                    </div>

                    {/* Playlist Drawer */}
                    <AnimatePresence>
                        {openDrawer && (
                            <motion.div
                                ref={drawerRef}
                                initial={{ bottom: '-18rem', opacity: 0 }}
                                animate={{ bottom: '6rem', opacity: 1 }}
                                exit={{ bottom: '-1rem', opacity: 0 }}
                                transition={{ duration: 0.2, ease: 'easeInOut' }}
                                className="absolute bottom-0 right-0 overflow-hidden flex justify-end px-1">
                                <div className="bg-secondary-700 text-white min-h-auto max-w-full sm:w-[408px] max-h-[480px] lg:max-h-[700px] rounded-lg overflow-y-auto">
                                    <PlayList />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Mobile Design */}
                    <div className="sm:hidden">
                        {/* Collapsed Player */}
                        {!mobilePlayerOpen && (
                            <div className="flex flex-col">
                                {
                                    isMobileView && (
                                        <ProgressBar />
                                    )
                                }
                                <div
                                    className="min-h-[88px] bg-secondary-audio gap-6 flex flex-row justify-between items-center text-white px-2 py-4"
                                >
                                    <div className="w-[70%] flex items-center" onClick={handleTileClick}>
                                        <Track />
                                    </div>
                                    <div className="w-[30%] flex items-center justify-end gap-4">
                                        <Controls isPlaying={isPlaying} setIsPlaying={setIsPlaying} />
                                        <button title="Close Player" onClick={() => onClose?.()}><Close className="text-white" /></button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Bottom Sheet */}
                        <AnimatePresence>
                            {mobilePlayerOpen && (
                                <motion.div
                                    initial={{ y: '100%' }}
                                    animate={{ y: 0 }}
                                    exit={{ y: '100%' }}
                                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                                    className="fixed bottom-0 left-0 right-0 bg-secondary-700 z-50 overflow-y-auto justify-center gap-4 flex flex-col text-white"
                                >
                                    <div className='flex flex-col h-[515px]'>
                                        <div className="relative">
                                            {/* Handle Bar */}
                                            <div className="sticky top-[0] left-0 right-0 flex items-center justify-center w-full pb-2 px-2">
                                                <button
                                                    className="bg-[#ffffff26] rounded-b-md w-12 flex justify-center text-gray-400"
                                                    onClick={() => setMobilePlayerOpen(false)}
                                                >
                                                    <img src={chevronDown} srcSet={`${chevronDown} 1x, ${chevronDown} 2x`} loading="lazy" alt="chevronDown" />
                                                </button>
                                            </div>

                                            {/* Content */}
                                            <div className="flex flex-col items-start mt-[1.5rem] px-8">
                                                <div
                                                    className="w-full h-[42vh] bg-cover bg-center"
                                                    style={{ backgroundImage: `url(${currentTrack?.thumbnail})` }}
                                                />
                                                <h4 className="text-xl font-semibold mt-2 line-clamp-1 font-inter">
                                                    {currentTrack?.title}
                                                </h4>
                                                <p className="text-base text-gray-400 font-inter" dangerouslySetInnerHTML={{ __html: currentTrack?.description }}></p>
                                            </div>

                                            {/* Controls Section */}
                                            <div className="flex items-center justify-center gap-4 mt-4">
                                                <Button
                                                    rounded="full"
                                                    variant="primary"
                                                    className={` bg-secondary-600  px-4 py-2.5 focus:bg-secondary-600 active:bg-secondary-600 `}
                                                    children={undefined}
                                                    onClick={() => toggleMediaToFavorite(currentTrack?.isFavorite ? 'Remove' : 'Add', 'Episode', Number(currentTrack?.id))}
                                                    isIconOnly
                                                    icon={
                                                        <SvgIcon color={currentTrack?.isFavorite ? 'red' : 'text-white'} className={`w-5 h-5`}
                                                            name={currentTrack?.isFavorite ? "linearHeartFilled" : "linearHeart"} />}
                                                />
                                                <span className='px-4 py-2.5 bg-secondary-600 rounded-3xl'>
                                                    <SpeedControl
                                                        toggleSpeedMenu={toggleSpeedMenu}
                                                        speedMenuOpen={speedMenuOpen}
                                                    />
                                                </span>
                                                <Button rounded="full" className='bg-secondary-600 px-4 py-2.5 focus:bg-secondary-600 active:bg-secondary-600' children={undefined} onClick={() => console.log('Shared')} isIconOnly icon={<SvgIcon className="w-5 h-5" name="shareWhite" />} />
                                            </div>

                                            {/* Progress Section */}
                                            <div className="px-4 mt-3">
                                                <ProgressBar />
                                                <div className="flex justify-between text-sm mt-2">
                                                    <span>
                                                        {Math.floor(timeProgress / 60)}:
                                                        {Math.floor(timeProgress % 60).toString().padStart(2, '0')}
                                                    </span>
                                                    <span>
                                                        {Math.floor((duration ?? 0) / 60)}:
                                                        {Math.floor((duration ?? 0) % 60).toString().padStart(2, '0')}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Playback Controls */}
                                            <div className="flex justify-around items-center my-5">
                                                <Controls isMobileView={isMobileView} isPlaying={isPlaying} setIsPlaying={setIsPlaying} />
                                            </div>
                                            {/* Playlist */}
                                            <div className="px-4 mb-20">
                                                <h4 className="text-lg font-semibold">Playlist</h4>
                                                <PlayList />
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};